<template>
  <div class="manage-controllers-tab">
    <div class="controller-status-legend">
      <div class="controller-status-legend__item">
        <span class="controller-status-legend__indicator controller-status-legend__indicator--online" />
        Online and ready to be used
      </div>

      <div class="controller-status-legend__item">
        <span class="controller-status-legend__indicator controller-status-legend__indicator--offline" />
        Offline (no network connection)
      </div>
    </div>

    <div v-if="isLoading && !devices.length" class="loading loading--inline">
      <i class="fas fa-spinner fa-spin"></i>
    </div>

    <div v-else-if="!devices.length" class="empty-state text-center py-8">
      No controllers have been registered for this account.
    </div>

    <div v-else class="screen-list clearfix">
      <div class="production-screens">
        <div class="mb-3">
          <h2 class="mb-0 tb-label">This Production</h2>
        </div>

        <draggable
          class="screens-draggable draggable"
          :class="productionControllers.length ? '' : 'screens-draggable--empty'"
          v-bind="{ sort: false, animation: 200, group: { name: 'controllers' } }"
          revert-on-spill="true"
          data-test="production-outputsModal-controllers-productionDroparea"
          @add="assignToProduction"
        >
          <production-controller-card
            v-for="device in productionControllers"
            :key="device.id"
            :device="device"
            :data-id="device.id"
            :current-production-id="currentProductionId"
            :is-connected="isProductionControllerConnected(device)"
            :is-saving="renameInProgressByDeviceId[device.id] || false"
            :data-test="`production-outputsModal-controllers-${device.id}`"
            @save="renameController"
          />
        </draggable>
        <div v-if="!productionControllers.length" class="empty-prompt text-center italic text-sm mb-4">
          Drag a controller here to assign it to this production.
        </div>
        <div slot="footer" class="screens-droparea droparea text-center">
          <i class="fas fa-arrow-down"></i>
        </div>
      </div>

      <div class="other-screens">
        <div class="mb-3">
          <h2 class="mb-0 tb-label">Other Controllers</h2>
        </div>

        <draggable
          class="screens-draggable draggable"
          :class="otherControllers.length ? '' : 'screens-draggable--empty'"
          v-bind="{ sort: false, animation: 200, group: { name: 'controllers' } }"
          revert-on-spill="true"
          data-test="production-outputsModal-controllers-otherDroparea"
          @add="unassignFromProduction"
        >
          <production-controller-card
            v-for="device in otherControllers"
            :key="device.id"
            :device="device"
            :data-id="device.id"
            :current-production-id="currentProductionId"
            :is-connected="isProductionControllerConnected(device)"
            :is-saving="renameInProgressByDeviceId[device.id] || false"
            :data-test="`production-outputsModal-controllers-${device.id}`"
            @save="renameController"
          />
        </draggable>

        <div v-if="!otherControllers.length" class="empty-prompt text-center py-4">
          All registered controllers are assigned to this production.
        </div>

        <div slot="footer" class="screens-droparea droparea text-center">
          <i class="fas fa-arrow-down"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ProductionControllerCard from './ProductionControllerCard.vue';

export default {
  name: 'ManageControllersTab',

  components: {
    ProductionControllerCard,
  },

  mounted() {
    this.load();
  },

  computed: {
    ...mapState({
      production: (state) => state.production.production,
      devices: (state) => state.productionControllers.devices,
      isLoading: (state) => state.productionControllers.isLoading,
      assignmentInProgressByDeviceId: (state) => state.productionControllers.assignmentInProgressByDeviceId,
      renameInProgressByDeviceId: (state) => state.productionControllers.renameInProgressByDeviceId,
    }),
    ...mapGetters([
      'productionControllersForCurrentProduction',
      'otherProductionControllers',
      'isProductionControllerConnected',
    ]),

    currentProductionId() {
      return this.production?.id || this.production?._id || null;
    },
    productionControllers() {
      return this.productionControllersForCurrentProduction;
    },
    otherControllers() {
      return this.otherProductionControllers;
    },
  },

  methods: {
    ...mapActions([
      'getProductionControllerDevices',
      'updateProductionControllerDeviceAssignment',
      'updateProductionControllerDevice',
    ]),

    async load() {
      try {
        await this.getProductionControllerDevices();
      } catch (err) {
        console.error('Error getting controllers:', err);
        // Assuming a global errorToast method or similar exists
        if (this.errorToast) {
          this.errorToast('There was an error loading controllers. Please try again.');
        }
      }
    },

    async renameController({ deviceId, name }) {
      if (!deviceId || this.renameInProgressByDeviceId[deviceId]) return;

      try {
        await this.updateProductionControllerDevice({ deviceId, name });
      } catch (err) {
        if (this.errorToast) {
          this.errorToast('There was an error renaming the controller. Please try again.');
        }
      }
    },

    async assignToProduction(evt) {
      const id = evt.item?.dataset?.id;
      if (!id || this.assignmentInProgressByDeviceId[id]) return;

      try {
        await this.updateProductionControllerDeviceAssignment({
          deviceId: id,
          productionId: this.currentProductionId,
        });
      } catch (err) {
        if (this.errorToast) {
          this.errorToast('There was an error assigning the controller. Please try again.');
        }

        await this.load();
      }
    },

    async unassignFromProduction(evt) {
      const id = evt.item?.dataset?.id;
      if (!id || this.assignmentInProgressByDeviceId[id]) return;

      try {
        await this.updateProductionControllerDeviceAssignment({
          deviceId: id,
          productionId: null,
        });
      } catch (err) {
        if (this.errorToast) {
          this.errorToast('There was an error unassigning the controller. Please try again.');
        }

        await this.load();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.controller-status-legend {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  color: #a0a4aa;
  font-size: 0.85rem;
}

.controller-status-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.controller-status-legend__indicator {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;

  &--online {
    background: #00d483;
  }

  &--offline {
    background: #e65447;
  }
}

.screen-list .tb-label {
  margin-bottom: 0;
}

.screens-draggable--empty {
  padding-bottom: 3rem;
  margin-bottom: -3rem;
  z-index: 1;
  position: relative;
}

.empty-prompt {
  color: #a0aec0; // approx tailwind gray-500
}
</style>
